import { motion } from "framer-motion";
import { Shield, Award, Microscope, Lock } from "lucide-react";
import AnimatedBackground from "../../components/AnimatedBackground";

const patents = [
  {
    icon: Microscope,
    title: "Error Classification Engine",
    description:
      "Our unique algorithm goes beyond 'Right or Wrong.' It distinguishes between conceptual gaps, calculation errors, and strategic flaws with board-level nuance.",
    patent: "Badge: Proprietary Logic",
  },
  {
    icon: Shield,
    title: "Board-Aligned Calibration",
    description:
      "Engineered to mimic the strict grading rubrics of CBSE and ICSE examiners, ensuring your 'AI Grade' matches your 'Real Grade'.",
    patent: "Technology: Board-Sync™",
  },
  {
    icon: Award,
    title: "NEP 2020 Skill Mapping",
    description:
      "Maps every answer to a competency matrix aligned with India's NEP 2020 guidelines, focusing on core understanding rather than rote memorization.",
    patent: "Framework: CompetencyGraph™",
  },
];

export default function PatentSection() {
  return (
    <section
      data-testid="patent-section"
      className="relative py-16 md:py-24 bg-card/50 overflow-hidden"
    >
      {/* Subtle Background */}
      <div className="absolute inset-0 opacity-30">
        <AnimatedBackground variant="subtle" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section Header - Shorter */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-4">
            <Lock className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium text-primary">
              Innovation & IP
            </span>
          </div>

          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
            Proprietary technology
            <span className="text-primary ml-2">built for precision</span>
          </h2>

          <p className="text-base text-muted-foreground max-w-xl mx-auto">
            Our AI methodology is protected by patents and built on years of research.
          </p>
        </motion.div>

        {/* Patent Cards - Smaller */}
        <div className="grid md:grid-cols-3 gap-6">
          {patents.map((patent, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.15, duration: 0.5 }}
              data-testid={`patent-${index}`}
              className="group relative bg-card rounded-xl border border-border p-6 transition-all duration-500 overflow-hidden hover:border-primary/40"
            >
              <div className="relative z-10">
                {/* Icon Container - Smaller */}
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors duration-300">
                  <patent.icon className="w-5 h-5 text-primary" />
                </div>

                {/* Content - Smaller */}
                <h3 className="text-lg font-semibold mb-2 group-hover:text-primary transition-colors">
                  {patent.title}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                  {patent.description}
                </p>

                {/* Patent Tag */}
                <div className="inline-flex items-center px-3 py-1.5 rounded-md bg-muted text-xs font-mono text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary transition-colors">
                  {patent.patent}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Trust Badges - Compact */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5, duration: 0.5 }}
          className="mt-12 flex flex-wrap items-center justify-center gap-6"
        >
          <div className="flex items-center gap-2 text-muted-foreground text-sm">
            <Shield className="w-4 h-4 text-primary" />
            <span>ISO 27001 Compliant</span>
          </div>
          <div className="flex items-center gap-2 text-muted-foreground text-sm">
            <Lock className="w-4 h-4 text-primary" />
            <span>GDPR Ready</span>
          </div>
          <div className="flex items-center gap-2 text-muted-foreground text-sm">
            <Award className="w-4 h-4 text-primary" />
            <span>StartupIndia Recognized</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
